import React, { useState, useEffect, useRef } from "react";
import * as SubframeCore from "@subframe/core";
import { Button, TextField, DropdownMenu } from "../ui";
import {
  FeatherChevronDown,
  FeatherBuilding,
  FeatherLoader,
  FeatherAlertTriangle,
} from "@subframe/core";
import { BankAccount, checkTransactionExists } from "../services/api";
import { SearchableDropdown } from "../shared/presentation/components";

interface VendorTransferToAccountRecoProps {
  currentBankAccountId: string;
  currentBankAccountName: string;
  allBankAccounts: BankAccount[];
  transactionDate: string;
  transactionAmount: number;
  currentTransactionId: string;
  onToAccountChange: (accountId: string) => void;
  onDateChange: (date: string) => void;
  onReferenceNumberChange: (refNumber: string) => void;
  onTransactionExistsChange?: (exists: boolean) => void;
  onToTransactionIdChange?: (transactionId: string) => void;
  onToAccountZohoBankAccountIdChange?: (zohoBankAccountId: string) => void;
  zohoGl?: string;
}

const VendorTransferToAccountReco: React.FC<
  VendorTransferToAccountRecoProps
> = ({
  currentBankAccountId,
  currentBankAccountName,
  allBankAccounts,
  transactionDate,
  transactionAmount,
  currentTransactionId,
  onToAccountChange,
  onDateChange,
  onReferenceNumberChange,
  onTransactionExistsChange,
  onToTransactionIdChange,
  onToAccountZohoBankAccountIdChange,
  zohoGl,
}) => {
  const [selectedToAccount, setSelectedToAccount] =
    useState<BankAccount | null>(null);
  const [date, setDate] = useState<string>("");
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [isCheckingTransaction, setIsCheckingTransaction] =
    useState<boolean>(false);
  const [transactionExists, setTransactionExists] = useState<boolean>(false);
  const [toAccountTransactionId, setToAccountTransactionId] =
    useState<string>("");
  const [transactionDetails, setTransactionDetails] = useState<{
    amount: string;
    currency_symbol: string;
    date: string;
    account_name: string;
  } | null>(null);
  const autoPopulatedRef = useRef(false);

  // Initialize date with transaction date
  useEffect(() => {
    if (transactionDate) {
      // Convert transaction date to YYYY-MM-DD format for input
      const formattedDate = new Date(transactionDate)
        .toISOString()
        .split("T")[0];
      setDate(formattedDate);
      onDateChange(formattedDate);
    }
  }, [transactionDate, onDateChange]);

  // Filter out the current bank account from the list
  const availableBankAccounts = allBankAccounts.filter(
    (account) => account.id !== currentBankAccountId
  );

  // Reset account when zohoGl changes (including when it becomes null or changes to a different value)
  useEffect(() => {
    // Reset the ref so we can auto-populate again if zohoGl changes
    autoPopulatedRef.current = false;

    if (!zohoGl) {
      setSelectedToAccount(null);
      return;
    }
  }, [zohoGl]);

  // Auto-populate account based on zoho_gl
  useEffect(() => {
    if (
      !zohoGl ||
      autoPopulatedRef.current ||
      availableBankAccounts.length === 0
    )
      return;

    const matchingAccount = availableBankAccounts.find(
      (account) => account.zoho_bankaccountid === zohoGl
    );

    if (matchingAccount) {
      setSelectedToAccount(matchingAccount);
      onToAccountChange(matchingAccount.id);
      if (onToAccountZohoBankAccountIdChange) {
        onToAccountZohoBankAccountIdChange(matchingAccount.zoho_bankaccountid);
      }
      autoPopulatedRef.current = true;

      // Check if transaction exists when account is auto-selected
      if (date && transactionAmount) {
        checkTransactionExistsHandler(
          matchingAccount.zoho_bankaccountid,
          date,
          transactionAmount
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zohoGl, availableBankAccounts]);

  const checkTransactionExistsHandler = async (
    accountId: string,
    date: string,
    amount: number
  ) => {
    setIsCheckingTransaction(true);
    setTransactionExists(false);
    setToAccountTransactionId("");
    setTransactionDetails(null);

    try {
      const result = await checkTransactionExists(
        accountId,
        date,
        amount,
        "credit"
      );
      if (result.data && result.data?.success && result.data?.exists) {
        setTransactionExists(true);
        // Store the transaction_id if present
        if (result.data?.transaction_id) {
          setToAccountTransactionId(result.data.transaction_id);
          console.log("To Account Transaction ID:", result.data.transaction_id);
          // Notify parent component
          if (onToTransactionIdChange) {
            onToTransactionIdChange(result.data.transaction_id);
          }
        }

        // Store transaction details if present
        if (result.data?.data) {
          setTransactionDetails({
            amount: result.data.data.amount || "",
            currency_symbol: result.data.data.currency_symbol || "₹",
            date: result.data.data.date || "",
            account_name: result.data.data.account_name || "",
          });
        }

        if (onTransactionExistsChange) {
          onTransactionExistsChange(true);
        }
      } else {
        setTransactionExists(false);
        setToAccountTransactionId("");
        setTransactionDetails(null);
        if (onTransactionExistsChange) {
          onTransactionExistsChange(false);
        }
        if (onToTransactionIdChange) {
          onToTransactionIdChange("");
        }
      }
    } catch (error) {
      console.error("Error checking transaction existence:", error);
      setTransactionExists(false);
      setToAccountTransactionId("");
      setTransactionDetails(null);
      if (onTransactionExistsChange) {
        onTransactionExistsChange(false);
      }
      if (onToTransactionIdChange) {
        onToTransactionIdChange("");
      }
    } finally {
      setIsCheckingTransaction(false);
    }
  };

  const handleToAccountSelect = (account: BankAccount) => {
    setSelectedToAccount(account);
    onToAccountChange(account.id);

    // Pass the zoho_bankaccountid to parent
    if (onToAccountZohoBankAccountIdChange) {
      onToAccountZohoBankAccountIdChange(account.zoho_bankaccountid);
    }

    // Check if transaction exists when account is selected
    if (date && transactionAmount) {
      checkTransactionExistsHandler(
        account.zoho_bankaccountid,
        date,
        transactionAmount
      );
    }
  };

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
    onDateChange(newDate);
  };

  const handleReferenceNumberChange = (refNum: string) => {
    setReferenceNumber(refNum);
    onReferenceNumberChange(refNum);
  };

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex w-full items-center gap-4">
        <div className="flex flex-col items-start gap-2 flex-1">
          <span className="text-body-bold font-body-bold text-default-font">
            From Account
          </span>
          <TextField className="w-full" label="" helpText="">
            <TextField.Input
              value={currentBankAccountName}
              disabled
              placeholder="Current bank account"
            />
          </TextField>
        </div>

        <div className="flex flex-col items-start gap-2 flex-1">
          <span className="text-body-bold font-body-bold text-default-font">
            To Account <span className="text-error-600">*</span>
          </span>
          <SearchableDropdown
            items={availableBankAccounts}
            loading={false}
            trigger={
              <Button
                className="w-full justify-between"
                variant="neutral-secondary"
                iconRight={<FeatherChevronDown />}
              >
                <span className="truncate text-left">
                  {selectedToAccount
                    ? selectedToAccount.bank_name
                    : "Select account"}
                </span>
              </Button>
            }
            getSearchableText={(account) => account.bank_name}
            renderItem={(account) => account.bank_name}
            getItemKey={(account) => account.id}
            onSelect={handleToAccountSelect}
            selectedKey={selectedToAccount?.id}
            searchPlaceholder="Search bank accounts..."
            emptyMessage="No other accounts available"
            noResultsMessage="No accounts match your search"
            dropdownClassName="min-w-[300px]"
          />
        </div>
      </div>

      <div className="flex w-full items-center gap-4">
        <div className="flex flex-col items-start gap-2 flex-1">
          <span className="text-body-bold font-body-bold text-default-font">
            Date <span className="text-error-600">*</span>
          </span>
          <TextField className="w-full" label="" helpText="">
            <TextField.Input
              type="date"
              value={date}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleDateChange(event.target.value)
              }
            />
          </TextField>
        </div>

        <div className="flex flex-col items-start gap-2 flex-1">
          <span className="text-body-bold font-body-bold text-default-font">
            Reference Number
          </span>
          <TextField className="w-full" label="" helpText="">
            <TextField.Input
              placeholder="Enter reference number (optional)"
              value={referenceNumber}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleReferenceNumberChange(event.target.value)
              }
            />
          </TextField>
        </div>
      </div>

      {/* Transaction Checking Status */}
      {selectedToAccount && (
        <div className="flex w-full flex-col items-center gap-3 py-2">
          {isCheckingTransaction ? (
            <div className="flex items-center gap-2">
              <FeatherLoader className="h-4 w-4 animate-spin text-brand-600" />
              <span className="text-body font-body text-neutral-500">
                Checking if the current transaction exists...
              </span>
            </div>
          ) : transactionExists ? (
            <>
              <div className="flex items-center gap-2">
                <span className="text-body-bold font-body-bold text-success-600">
                  ✓ Transaction found in the selected account
                </span>
              </div>
              {transactionDetails && (
                <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-success-200 bg-success-50 px-4 py-3">
                  <span className="text-caption-bold font-caption-bold text-success-700">
                    Transaction Details:
                  </span>
                  <div className="flex w-full items-center justify-between gap-4">
                    <div className="flex flex-col items-start">
                      <span className="text-caption font-caption text-neutral-500">
                        Amount
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {transactionDetails.currency_symbol}
                        {parseFloat(transactionDetails.amount).toLocaleString(
                          "en-IN"
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="text-caption font-caption text-neutral-500">
                        Date
                      </span>
                      <span className="text-body font-body text-default-font">
                        {new Date(transactionDetails.date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex flex-col items-start flex-1">
                      <span className="text-caption font-caption text-neutral-500">
                        Account
                      </span>
                      <span className="text-body font-body text-default-font">
                        {transactionDetails.account_name}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-body font-body text-warning-600">
                Transaction not found in the selected account
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VendorTransferToAccountReco;
